import { format, formatInTimeZone } from 'date-fns-tz';
import { parseISO, isValid } from 'date-fns';

/**
 * Global System Preferences for Date/Time Formatting
 */
export interface DateFormatSettings {
    timeZone: string;
    dateFormat: string;
    timeFormat: '12h' | '24h';
}

// Default settings if none provided
const defaultSettings: DateFormatSettings = {
    timeZone: 'UTC',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '24h',
};

/**
 * Formats a UTC date string or Date object based on system settings
 * 
 * @param date The date to format (string or Date object)
 * @param settings System settings for formatting
 * @returns Formatted date/time string
 */
export const formatDate = (
    date: string | Date | null | undefined,
    settings: DateFormatSettings = defaultSettings
): string => {
    if (!date) return '-';

    const d = typeof date === 'string' ? parseISO(date) : date;

    if (!isValid(d)) return '-';

    // Map settings to date-fns format tokens
    // MM/DD/YYYY -> MM/dd/yyyy
    // DD/MM/YYYY -> dd/MM/yyyy
    // YYYY-MM-DD -> yyyy-MM-dd
    // MMM D, YYYY -> MMM d, yyyy
    let datePattern = settings.dateFormat
        .replace('YYYY', 'yyyy')
        .replace('DD', 'dd');

    // Time pattern
    const timePattern = settings.timeFormat === '12h' ? 'hh:mm:ss a' : 'HH:mm:ss';

    const fullPattern = `${datePattern} ${timePattern}`;

    try {
        return formatInTimeZone(d, settings.timeZone, fullPattern);
    } catch (error) {
        console.error('Error formatting date:', error);
        return format(d, fullPattern); // Fallback to local time if timezone invalid
    }
};

/**
 * Convenience wrapper for formatting just the date part
 */
export const formatDateOnly = (
    date: string | Date | null | undefined,
    settings: DateFormatSettings = defaultSettings
): string => {
    if (!date) return '-';
    const d = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(d)) return '-';

    let datePattern = settings.dateFormat
        .replace('YYYY', 'yyyy')
        .replace('DD', 'dd');

    try {
        return formatInTimeZone(d, settings.timeZone, datePattern);
    } catch (error) {
        return format(d, datePattern);
    }
};

/**
 * Formats only the time portion of a date, respecting timezone and 12h/24h setting.
 * Use for "Last updated: HH:mm:ss" patterns.
 */
export const formatTimeOnly = (
    date: string | Date | null | undefined,
    settings: DateFormatSettings = defaultSettings
): string => {
    if (!date) return '-';
    const d = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(d)) return '-';

    const timePattern = settings.timeFormat === '12h' ? 'hh:mm:ss a' : 'HH:mm:ss';

    try {
        return formatInTimeZone(d, settings.timeZone, timePattern);
    } catch (error) {
        return format(d, timePattern);
    }
};

/**
 * Creates an ApexCharts tooltip x-axis formatter function
 * that uses the centralized date formatting settings.
 */
export const createChartTooltipFormatter = (
    settings: DateFormatSettings = defaultSettings
): ((val: number) => string) => {
    return (val: number) => {
        if (!val) return '-';
        return formatDate(new Date(val), settings);
    };
};
